import { getEnv } from '@/lib/utils'

const STORAGE_KEY = 'omtae.gateway.apiKey.session'
const LEGACY_STORAGE_KEY = 'omtae.gateway.apiKey'
let hasPromptedForGatewayKey = false

/** Header name the gateway expects (must match gateway `API_KEY_HEADER`, default `x-api-key`). */
export function gatewayAuthHeaderName(): string {
  return getEnv('VITE_GATEWAY_API_KEY_HEADER') || 'x-api-key'
}

/**
 * Shared secret for gateway HTTP API when AUTH_ENABLED=true.
 * Browser: localStorage. Build-time: VITE_GATEWAY_API_KEY (avoid for public sites).
 */
export function getGatewayApiKey(): string {
  if (typeof window === 'undefined') return getEnv('VITE_GATEWAY_API_KEY') || ''
  const fromStore = window.sessionStorage.getItem(STORAGE_KEY)
  if (fromStore) return fromStore
  const fromLegacy = window.localStorage.getItem(LEGACY_STORAGE_KEY)
  if (fromLegacy) return fromLegacy
  return getEnv('VITE_GATEWAY_API_KEY') || ''
}

export function setGatewayApiKey(value: string): void {
  if (typeof window === 'undefined') return
  const t = value.trim()
  if (t) {
    window.sessionStorage.setItem(STORAGE_KEY, t)
    window.localStorage.removeItem(LEGACY_STORAGE_KEY)
  } else {
    window.sessionStorage.removeItem(STORAGE_KEY)
    window.localStorage.removeItem(LEGACY_STORAGE_KEY)
  }
}

/**
 * Fetch the OMTAE gateway (any path). Adds API key header when set.
 * `urlOrPath` may be absolute (e.g. http://host:8080/llm/v1/models) or path (e.g. /coding/chat).
 */
export async function gatewayFetch(urlOrPath: string, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers)
  let key = getGatewayApiKey()
  if (!key && typeof window !== 'undefined' && !hasPromptedForGatewayKey) {
    hasPromptedForGatewayKey = true
    const entered = window.prompt('Enter gateway passcode')
    if (entered !== null) {
      setGatewayApiKey(entered)
      key = getGatewayApiKey()
    }
  }
  if (key) headers.set(gatewayAuthHeaderName(), key)

  let url = urlOrPath
  if (!urlOrPath.startsWith('http')) {
    const base = getEnv('VITE_GATEWAY_URL').replace(/\/$/, '')
    const path = urlOrPath.startsWith('/') ? urlOrPath : `/${urlOrPath}`
    url = base ? `${base}${path}` : path
  }

  let response = await fetch(url, { ...init, headers })
  if (response.status === 401 && typeof window !== 'undefined') {
    setGatewayApiKey('')
    const retry = window.prompt('Gateway passcode required. Enter gateway passcode')
    if (retry !== null) {
      setGatewayApiKey(retry)
      const retryHeaders = new Headers(init?.headers)
      const retryKey = getGatewayApiKey()
      if (retryKey) retryHeaders.set(gatewayAuthHeaderName(), retryKey)
      response = await fetch(url, { ...init, headers: retryHeaders })
    }
  }

  return response
}
